/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import InputWithButtons from './InputWithButtons';
import SearchableEntity from './SearchableEntity';

import { removeGroupFromField } from '../helpers/groupResultFields';
import DocumentService from '../../../services/DocumentService';
import {
  formatField,
  getFieldType,
  formatDocumentForUpdate,
  getOriginalFieldValue,
} from './helpers/format';
import { useImportDocumentStore } from '../../../store';

const searchableEntityKeys = [
  'Borrower',
  'Coborrower',
  'Agent',
  'Carrier',
  'Broker',
];

export default function EditableField({
  valueKey,
  value,
  formattedKey,
  fullKey,
  document,
  refetch,
  lenderId,
}) {
  const [newValue, setNewValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  const importDocumentChanges = useImportDocumentStore(
    (state) => state.importDocumentChanges
  );
  const setImportDocumentChanges = useImportDocumentStore(
    (state) => state.setImportDocumentChanges
  );

  const formattedValue = formatField(valueKey, value);
  const type = getFieldType(valueKey);

  const { mutate: updateDocument } = useMutation({
    mutationFn: (value) => {
      const currentNewValue = value?.value || newValue;

      const keyWithoutGroup = removeGroupFromField(fullKey);

      const [documentWithNewValue, formattedNewValue] = formatDocumentForUpdate(
        currentNewValue,
        keyWithoutGroup,
        type,
        document
      );

      const originalValue = getOriginalFieldValue(keyWithoutGroup, document);

      setImportDocumentChanges([
        ...importDocumentChanges,
        {
          field: keyWithoutGroup,
          original_value: originalValue,
          modified_value: formattedNewValue,
        },
      ]);

      return DocumentService.updateDocument({
        documentId: document.id,
        formValues: {
          ...documentWithNewValue,
        },
      });
    },
    onSuccess: () => {
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      console.log('Error updating document:', error);
    },
  });

  useEffect(() => {
    if (type === 'percentage') {
      setNewValue((parseFloat(value) * 100).toFixed(2));
    } else {
      setNewValue(value);
    }
  }, [value, type, isEditing]);

  if (!formattedValue || !formattedKey) {
    return null;
  }

  const isNotice = formattedKey === 'Notice';

  // const isEditable = isFieldEditable(
  //   valueKey,
  //   document?.parseur_mailbox_description,
  //   document?.parseur_result
  // );

  const shouldShowSearchableEntity =
    searchableEntityKeys.includes(formattedKey) &&
    !isNotice &&
    (document?.parseur_mailbox_description === 'NOTE' ||
      document?.parseur_mailbox_description === 'INSURANCE_BINDER');

  return (
    <li>
      <div className="flex items-center">
        <span className={`font-semibold ${isNotice ? 'bg-yellow-300' : ''}`}>
          {formattedKey}
        </span>
        :{' '}
        {!shouldShowSearchableEntity && (
          <InputWithButtons
            isNotice={isNotice}
            formattedValue={formattedValue}
            canFieldBeEdited
            type={type}
            value={newValue}
            setValue={setNewValue}
            onSubmit={updateDocument}
          />
        )}
        {shouldShowSearchableEntity && (
          <SearchableEntity
            name={formattedValue}
            lenderId={lenderId}
            type={formattedKey}
            docType={document?.parseur_mailbox_description}
            document={document}
            refetch={refetch}
            value={newValue}
            setValue={setNewValue}
            onSubmit={updateDocument}
          />
        )}
      </div>
    </li>
  );
}
